import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { Lesson, Question, Module } from 'core/models/course-content.model';
import { CourseViewerService } from 'core/services/course-viewer.service';
import {
  EvaluationResultsService,
  QuestionResult as ServiceQuestionResult,
} from 'core/services/evaluation-results.service';
import { LessonHeader } from 'components/lesson-header/lesson-header';

interface QuestionResult {
  correct: boolean;
  points: number;
}

@Component({
  selector: 'app-evaluation-content',
  standalone: true,
  imports: [CommonModule, LessonHeader],
  templateUrl: './evaluation.html',
  styleUrl: './evaluation.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'w-full h-full flex flex-col',
  },
})
export class Evaluation implements OnInit, OnChanges, OnDestroy {
  private readonly courseService = inject(CourseViewerService);
  private readonly evaluationResultsService = inject(EvaluationResultsService);

  @Input({ required: true }) lesson!: Lesson;
  @Input() module?: Module | null;
  private previousLessonId: string | null = null;

  ngOnInit(): void {
    // Garantir que o índice comece em 0 ao inicializar
    this.currentQuestionIndex.set(0);
    if (this.lesson?.id) {
      this.previousLessonId = this.lesson.id;
      // Carregar rascunhos salvos para esta lição
      this.loadDraftAnswers();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Resetar índice da questão quando a lição mudar
    if (changes['lesson']) {
      const currentLessonId = this.lesson?.id;
      if (currentLessonId && currentLessonId !== this.previousLessonId) {
        this.currentQuestionIndex.set(0);
        this.previousLessonId = currentLessonId;
        // Resetar estados de submissão
        this.isSubmitting.set(false);
        this.showSuccessMessage.set(false);
        // Carregar rascunhos salvos para a nova lição
        this.loadDraftAnswers();
      }
    }
  }

  // Estado interno da avaliação
  protected selectedAnswers = signal<Map<string, string[]>>(new Map());
  protected isSubmitted = signal<boolean>(false);
  protected isSubmitting = signal<boolean>(false);
  protected showSuccessMessage = signal<boolean>(false);
  protected results = signal<Map<string, QuestionResult>>(new Map());
  protected totalScore = signal<number>(0);
  protected currentQuestionIndex = signal<number>(0);
  private hasSavedResults = false;

  /**
   * Obtém os dados da avaliação
   */
  protected get evaluationData() {
    const data = this.lesson?.evaluationData;
    if (!data) {
      return null;
    }

    // Para avaliações, usar o texto da questão como título se houver apenas uma questão
    if (data.questions && data.questions.length === 1) {
      return {
        ...data,
        title: data.questions[0].text,
      };
    }

    return data;
  }

  /**
   * Obtém a lista de questões
   */
  protected get questions(): Question[] {
    return this.evaluationData?.questions || [];
  }

  /**
   * Obtém a questão atual
   */
  protected get currentQuestion(): Question | null {
    const questions = this.questions;
    const index = this.currentQuestionIndex();
    return questions[index] || null;
  }

  /**
   * Verifica se pode ir para a questão anterior
   */
  protected get canGoPrevious(): boolean {
    return this.currentQuestionIndex() > 0;
  }

  /**
   * Verifica se pode ir para a próxima questão
   */
  protected get canGoNext(): boolean {
    const index = this.currentQuestionIndex();
    return index < this.questions.length - 1;
  }

  /**
   * Verifica se é a última questão
   */
  protected get isLastQuestion(): boolean {
    const index = this.currentQuestionIndex();
    return index === this.questions.length - 1;
  }

  /**
   * Obtém o número da questão atual (1-indexed)
   */
  protected get currentQuestionNumber(): number {
    return this.currentQuestionIndex() + 1;
  }

  /**
   * Obtém o total de questões
   */
  protected get totalQuestions(): number {
    return this.questions.length;
  }

  /**
   * Obtém a porcentagem de progresso da questão atual
   */
  protected get currentQuestionProgress(): number {
    if (this.totalQuestions === 0) {
      return 0;
    }
    return Math.round((this.currentQuestionNumber / this.totalQuestions) * 100);
  }

  /**
   * Navega para a questão anterior
   */
  protected goToPreviousQuestion(): void {
    if (this.canGoPrevious) {
      this.saveCurrentQuestionAnswer();
      this.currentQuestionIndex.update((index) => Math.max(0, index - 1));
    }
  }

  /**
   * Navega para a próxima questão
   */
  protected goToNextQuestion(): void {
    if (this.canGoNext) {
      this.saveCurrentQuestionAnswer();
      this.currentQuestionIndex.update((index) =>
        Math.min(this.questions.length - 1, index + 1),
      );
    }
  }

  /**
   * Salva a resposta da questão atual automaticamente
   */
  private saveCurrentQuestionAnswer(): void {
    const question = this.currentQuestion;
    if (!question) {
      return;
    }

    const answers = this.selectedAnswers();
    const selected = answers.get(question.id) || [];

    // Salvar como rascunho
    this.saveDraftAnswer(question.id, selected);

    // Se há resposta selecionada, salva automaticamente
    if (selected.length > 0) {
      this.saveCurrentLessonResults();
    }
  }

  /**
   * Salva rascunho de uma resposta específica
   */
  private saveDraftAnswer(
    questionId: string,
    selectedOptionIds: string[],
  ): void {
    if (!this.lesson?.id) {
      return;
    }
    this.evaluationResultsService.saveDraft(
      this.lesson.id,
      questionId,
      selectedOptionIds,
    );
  }

  /**
   * Carrega rascunhos salvos para a lição atual
   * Também verifica se há resultados já submetidos para restaurar as respostas
   */
  private loadDraftAnswers(): void {
    if (!this.lesson?.id) {
      return;
    }

    // Primeiro, tentar carregar rascunhos salvos
    const draftAnswers = this.evaluationResultsService.getDraft(this.lesson.id);

    if (draftAnswers.size > 0) {
      // Restaurar respostas salvas dos rascunhos
      this.selectedAnswers.set(new Map(draftAnswers));

      // Verificar se há resultados já submetidos para esta questão
      const results = this.evaluationResultsService.getResults();
      if (results && results.questionResults) {
        // Restaurar respostas de questões já submetidas
        this.questions.forEach((question) => {
          const questionResult = results.questionResults.find(
            (r) => r.questionId === question.id,
          );
          if (questionResult && questionResult.selectedAnswers.length > 0) {
            // Usar respostas submetidas se existirem (sobrescreve rascunhos)
            const currentAnswers = new Map(this.selectedAnswers());
            currentAnswers.set(question.id, questionResult.selectedAnswers);
            this.selectedAnswers.set(currentAnswers);
          }
        });
      }
    } else {
      // Se não há rascunhos, verificar se há resultados submetidos
      const results = this.evaluationResultsService.getResults();
      if (results && results.questionResults) {
        const restoredAnswers = new Map<string, string[]>();
        this.questions.forEach((question) => {
          const questionResult = results.questionResults.find(
            (r) => r.questionId === question.id,
          );
          if (questionResult && questionResult.selectedAnswers.length > 0) {
            restoredAnswers.set(question.id, questionResult.selectedAnswers);
          }
        });

        if (restoredAnswers.size > 0) {
          this.selectedAnswers.set(restoredAnswers);
        } else {
          this.selectedAnswers.set(new Map());
        }
      } else {
        // Limpar respostas se não há rascunho nem resultados
        this.selectedAnswers.set(new Map());
      }
    }
  }

  /**
   * Alterna a seleção de uma resposta
   */
  protected toggleAnswer(questionId: string, optionId: string): void {
    if (this.isSubmitted()) {
      return; // Não permite alterar após submissão
    }

    const currentAnswers = new Map(this.selectedAnswers());
    const question = this.questions.find((q) => q.id === questionId);

    if (!question) {
      return;
    }

    const currentSelected = currentAnswers.get(questionId) || [];

    if (question.type === 'single-choice' || question.type === 'boolean') {
      // Seleção única: substitui a seleção anterior
      currentAnswers.set(questionId, [optionId]);
    } else {
      // Seleção múltipla: adiciona ou remove
      const index = currentSelected.indexOf(optionId);
      if (index > -1) {
        currentSelected.splice(index, 1);
      } else {
        currentSelected.push(optionId);
      }
      currentAnswers.set(questionId, [...currentSelected]);
    }

    this.selectedAnswers.set(new Map(currentAnswers));

    // Salvar como rascunho automaticamente
    this.saveDraftAnswer(questionId, currentAnswers.get(questionId) || []);
  }

  /**
   * Verifica se uma opção está selecionada
   */
  protected isSelected(questionId: string, optionId: string): boolean {
    const selected = this.selectedAnswers().get(questionId) || [];
    return selected.includes(optionId);
  }

  /**
   * Verifica se permite seleção múltipla
   */
  protected canSelectMultiple(questionType: string): boolean {
    return questionType === 'multiple-choice';
  }

  /**
   * Verifica se todas as questões foram respondidas
   */
  protected get allQuestionsAnswered(): boolean {
    if (this.questions.length === 0) {
      return false;
    }

    const answers = this.selectedAnswers();
    return this.questions.every((question) => {
      const selected = answers.get(question.id) || [];
      return selected.length > 0;
    });
  }

  /**
   * Verifica se a questão atual foi respondida
   */
  protected get currentQuestionAnswered(): boolean {
    const question = this.currentQuestion;
    if (!question) {
      return false;
    }

    const answers = this.selectedAnswers();
    const selected = answers.get(question.id) || [];
    return selected.length > 0;
  }

  /**
   * Verifica se é a última questão do módulo 5 (antes da lição de resultados)
   * Baseado nas questões reais do módulo, não nas lições convertidas
   */
  protected get isLastQuestionInModule5(): boolean {
    const course = this.courseService.getCourse();
    const module5 = course.modules.find((m) => m.id === 'module-5');

    if (
      !module5 ||
      module5.contentType !== 'evaluations' ||
      !module5.questions
    ) {
      return false;
    }

    // Verificar se a questão atual é a última questão do módulo
    if (this.questions.length === 0 || !this.currentQuestion) {
      return false;
    }

    // Ordenar questões pelo order e verificar se a questão atual é a última
    const sortedQuestions = [...module5.questions].sort(
      (a, b) => a.order - b.order,
    );
    const lastQuestion = sortedQuestions[sortedQuestions.length - 1];

    return this.currentQuestion.id === lastQuestion.id;
  }

  /**
   * Submete as respostas e calcula a pontuação
   */
  protected submitAnswers(): void {
    if (
      !this.allQuestionsAnswered ||
      this.isSubmitted() ||
      this.isSubmitting()
    ) {
      return;
    }

    this.isSubmitting.set(true);

    const answers = this.selectedAnswers();
    const newResults = new Map<string, QuestionResult>();
    const questionResults: ServiceQuestionResult[] = [];
    let totalScore = 0;

    // Calcular pontuação apenas para as questões da lição atual
    this.questions.forEach((question) => {
      const selected = answers.get(question.id) || [];
      const correctAnswers = question.correctAnswers;

      // Calcular contagem de respostas corretas e incorretas selecionadas
      const correctCount = selected.filter((id) =>
        correctAnswers.includes(id),
      ).length;
      const incorrectCount = selected.filter(
        (id) => !correctAnswers.includes(id),
      ).length;
      const totalCorrect = correctAnswers.length;

      // Verifica se a resposta está correta
      // Para múltipla escolha: deve bater EXATAMENTE com todas as alternativas corretas
      // Exemplo: se corretas são [a, b, d, e], o aluno deve marcar exatamente [a, b, d, e]
      let isCorrect = false;

      if (question.type === 'single-choice' || question.type === 'boolean') {
        // Para seleção única: deve ter exatamente uma resposta selecionada e deve ser a correta
        isCorrect =
          selected.length === 1 &&
          correctAnswers.length === 1 &&
          selected[0] === correctAnswers[0];
      } else if (question.type === 'multiple-choice') {
        // Para seleção múltipla: deve bater EXATAMENTE com todas as alternativas corretas
        // 1. O número de respostas selecionadas deve ser igual ao número de respostas corretas
        // 2. Todas as respostas selecionadas devem estar nas corretas (não pode ter incorretas)
        // 3. Todas as respostas corretas devem estar nas selecionadas (não pode faltar nenhuma)
        // Isso garante que se corretas são [a, b, d, e], o aluno precisa marcar exatamente [a, b, d, e]
        isCorrect =
          selected.length === correctAnswers.length && // Mesmo número de seleções
          selected.length > 0 && // Pelo menos uma seleção
          selected.every((id) => correctAnswers.includes(id)) && // Todas selecionadas estão corretas
          correctAnswers.every((id) => selected.includes(id)); // Todas corretas estão selecionadas
      }

      const points = isCorrect ? question.points : 0;
      totalScore += points;

      newResults.set(question.id, {
        correct: isCorrect,
        points,
      });

      questionResults.push({
        questionId: question.id,
        lessonId: this.lesson.id,
        correct: isCorrect,
        points,
        selectedAnswers: [...selected],
        correctCount,
        incorrectCount,
        totalCorrect,
      });
    });

    this.results.set(newResults);
    this.totalScore.set(totalScore);
    this.isSubmitted.set(true);

    // Não limpar rascunhos após submissão - manter respostas para o aluno poder revisar
    // As respostas já estão salvas e serão carregadas quando o aluno voltar para a questão

    // Sempre calcular pontuação total baseada em todas as questões do módulo 5
    // Obter resultados anteriores e acumular, removendo duplicatas por questionId
    const previousResults = this.evaluationResultsService.getResults();
    const existingResults = previousResults?.questionResults || [];

    // Remover resultados da questão atual se já existirem (para evitar duplicatas)
    // Usar questionId como chave única, não lessonId
    const filteredExisting = existingResults.filter(
      (r) => !questionResults.some((qr) => qr.questionId === r.questionId),
    );

    // Mesclar com os novos resultados
    const allQuestionResults: ServiceQuestionResult[] = [
      ...filteredExisting,
      ...questionResults,
    ];

    // Calcular pontuação total de todas as questões do módulo 5
    const course = this.courseService.getCourse();
    const module5 = course.modules.find((m) => m.id === 'module-5');
    let totalPointsAllQuestions = 0;
    let totalScoreAllQuestions = 0;

    if (module5 && module5.contentType === 'evaluations' && module5.questions) {
      // Calcular pontos totais de todas as questões do módulo
      totalPointsAllQuestions = module5.questions.reduce(
        (sum, q) => sum + q.points,
        0,
      );

      // Calcular pontuação obtida de todas as questões respondidas
      module5.questions.forEach((question) => {
        const result = allQuestionResults.find(
          (r) => r.questionId === question.id,
        );
        if (result && result.correct) {
          totalScoreAllQuestions += result.points;
        }
      });
    }

    const percentage =
      totalPointsAllQuestions > 0
        ? Math.round((totalScoreAllQuestions / totalPointsAllQuestions) * 100)
        : 0;

    // Sempre salvar resultados com cálculo baseado em todas as questões
    this.evaluationResultsService.saveResults({
      lessonId: this.lesson.id,
      totalScore: totalScoreAllQuestions,
      totalPoints: totalPointsAllQuestions,
      percentage,
      questionResults: allQuestionResults,
      submittedAt: new Date(),
    });

    // Se for a última questão, mostrar mensagem e navegar para resultados
    if (this.isLastQuestionInModule5) {
      // Mostrar mensagem de sucesso após processamento
      setTimeout(() => {
        this.isSubmitting.set(false);
        this.showSuccessMessage.set(true);
      }, 500);

      // Delay antes de navegar para a próxima lição (resultados)
      setTimeout(() => {
        const navigation = this.courseService.getNavigation();
        if (navigation.canGoNext) {
          this.courseService.goToNextLesson();
        }
      }, 2000);
    } else {
      // Resetar estado de submissão após salvar
      setTimeout(() => {
        this.isSubmitting.set(false);
      }, 500);
    }
  }

  /**
   * Salva os resultados da lição atual automaticamente
   * Sempre calcula baseado em todas as questões do módulo 5
   */
  private saveCurrentLessonResults(): void {
    if (this.hasSavedResults || this.isSubmitted()) {
      return;
    }

    const answers = this.selectedAnswers();
    if (answers.size === 0) {
      return; // Não há respostas para salvar
    }

    const questionResults: ServiceQuestionResult[] = [];

    // Processar apenas as questões da lição atual
    this.questions.forEach((question) => {
      const selected = answers.get(question.id) || [];
      if (selected.length === 0) {
        return; // Questão não respondida
      }

      const correctAnswers = question.correctAnswers;

      // Calcular contagem de respostas corretas e incorretas selecionadas
      const correctCount = selected.filter((id) =>
        correctAnswers.includes(id),
      ).length;
      const incorrectCount = selected.filter(
        (id) => !correctAnswers.includes(id),
      ).length;
      const totalCorrect = correctAnswers.length;

      // Verifica se a resposta está correta
      // Para múltipla escolha: deve bater EXATAMENTE com todas as alternativas corretas
      let isCorrect = false;
      if (question.type === 'single-choice' || question.type === 'boolean') {
        // Para seleção única: deve ter exatamente uma resposta selecionada e deve ser a correta
        isCorrect =
          selected.length === 1 &&
          correctAnswers.length === 1 &&
          selected[0] === correctAnswers[0];
      } else if (question.type === 'multiple-choice') {
        // Para seleção múltipla: deve bater EXATAMENTE com todas as alternativas corretas
        // Exemplo: se corretas são [a, b, d, e], o aluno deve marcar exatamente [a, b, d, e]
        isCorrect =
          selected.length === correctAnswers.length && // Mesmo número de seleções
          selected.length > 0 && // Pelo menos uma seleção
          selected.every((id) => correctAnswers.includes(id)) && // Todas selecionadas estão corretas
          correctAnswers.every((id) => selected.includes(id)); // Todas corretas estão selecionadas
      }

      const points = isCorrect ? question.points : 0;

      questionResults.push({
        questionId: question.id,
        lessonId: this.lesson.id,
        correct: isCorrect,
        points,
        selectedAnswers: [...selected],
        correctCount,
        incorrectCount,
        totalCorrect,
      });
    });

    if (questionResults.length > 0) {
      // Obter resultados anteriores e mesclar por questionId (não por lessonId)
      const previousResults = this.evaluationResultsService.getResults();
      const existingResults = previousResults?.questionResults || [];

      // Remover resultados das questões atuais se já existirem (para evitar duplicatas)
      const filteredExisting = existingResults.filter(
        (r) => !questionResults.some((qr) => qr.questionId === r.questionId),
      );

      const allQuestionResults = [...filteredExisting, ...questionResults];

      // Calcular pontuação total baseada em todas as questões do módulo 5
      const course = this.courseService.getCourse();
      const module5 = course.modules.find((m) => m.id === 'module-5');
      let totalPointsAllQuestions = 0;
      let totalScoreAllQuestions = 0;

      if (
        module5 &&
        module5.contentType === 'evaluations' &&
        module5.questions
      ) {
        // Calcular pontos totais de todas as questões do módulo
        totalPointsAllQuestions = module5.questions.reduce(
          (sum, q) => sum + q.points,
          0,
        );

        // Calcular pontuação obtida de todas as questões respondidas
        module5.questions.forEach((question) => {
          const result = allQuestionResults.find(
            (r) => r.questionId === question.id,
          );
          if (result && result.correct) {
            totalScoreAllQuestions += result.points;
          }
        });
      }

      const percentage =
        totalPointsAllQuestions > 0
          ? Math.round((totalScoreAllQuestions / totalPointsAllQuestions) * 100)
          : 0;

      this.evaluationResultsService.saveResults({
        lessonId: this.lesson.id,
        totalScore: totalScoreAllQuestions,
        totalPoints: totalPointsAllQuestions,
        percentage,
        questionResults: allQuestionResults,
        submittedAt: new Date(),
      });

      this.hasSavedResults = true;
    }
  }

  /**
   * Reseta a avaliação
   */
  protected resetEvaluation(): void {
    this.selectedAnswers.set(new Map());
    this.isSubmitted.set(false);
    this.isSubmitting.set(false);
    this.showSuccessMessage.set(false);
    this.results.set(new Map());
    this.totalScore.set(0);
    this.hasSavedResults = false;
    this.evaluationResultsService.clearResults();
  }

  ngOnDestroy(): void {
    // Salvar todas as respostas como rascunho antes de sair
    if (!this.isSubmitted() && this.lesson?.id) {
      const answers = this.selectedAnswers();
      answers.forEach((selectedOptionIds, questionId) => {
        if (selectedOptionIds.length > 0) {
          this.saveDraftAnswer(questionId, selectedOptionIds);
        }
      });
    }

    // Salvar resultados automaticamente ao sair da lição (se não foi submetido ainda)
    if (!this.isSubmitted() && !this.isLastQuestionInModule5) {
      this.saveCurrentLessonResults();
    }
  }

  /**
   * Obtém o resultado de uma questão específica
   */
  protected getQuestionResult(questionId: string): QuestionResult | null {
    return this.results().get(questionId) || null;
  }

  /**
   * Verifica se uma questão está correta
   */
  protected isQuestionCorrect(questionId: string): boolean {
    const result = this.getQuestionResult(questionId);
    return result?.correct || false;
  }

  /**
   * Obtém a porcentagem de acerto
   */
  protected get percentage(): number {
    const totalPoints = this.evaluationData?.totalPoints || 0;
    if (totalPoints === 0) {
      return 0;
    }
    return Math.round((this.totalScore() / totalPoints) * 100);
  }

  /**
   * Obtém a mensagem de feedback baseada na porcentagem
   */
  protected get feedbackMessage(): string {
    const percentage = this.percentage;

    if (percentage === 100) {
      return 'Parabéns! Você acertou todas as questões!';
    } else if (percentage >= 80) {
      return 'Excelente! Você teve um ótimo desempenho!';
    } else if (percentage >= 60) {
      return 'Bom trabalho! Continue estudando para melhorar ainda mais.';
    } else if (percentage >= 40) {
      return 'Você está no caminho certo. Revise o conteúdo e tente novamente.';
    } else {
      return 'Não desista! Revise o material e tente novamente.';
    }
  }

  /**
   * Obtém a classe CSS para feedback visual de uma opção
   */
  protected getOptionClass(questionId: string, optionId: string): string {
    // Apenas destaca opções selecionadas (feedback será mostrado na lição de resultados)
    return this.isSelected(questionId, optionId)
      ? 'bg-gray-200 dark:bg-neutral-700 border-gray-300 dark:border-neutral-600'
      : 'bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700';
  }
}
